import type { ApolloClient, NormalizedCacheObject } from "@apollo/client";

/**
 * Dependency-free holder for the federation client singletons.
 *
 * Kept separate from `client/index.ts` so the global test teardown can reset the
 * singletons (stop in-flight queries, drop references) by importing ONLY this
 * module. Importing `client/index.ts` would pull in `@compono/ui`, `~/env` and
 * the auth context, colliding with per-test module mocks of those in unrelated
 * suites. The only value import here is erased (`import type`), so this module
 * has no runtime dependencies.
 */

type FederationClient = ApolloClient<NormalizedCacheObject>;

let authedClient: FederationClient | undefined;
let platformClient: FederationClient | undefined;
let publicClient: FederationClient | undefined;

export const getStoredAuthedClient = (): FederationClient | undefined =>
  authedClient;

export const storeAuthedClient = (client: FederationClient): void => {
  authedClient = client;
};

export const getStoredPlatformClient = (): FederationClient | undefined =>
  platformClient;

export const storePlatformClient = (client: FederationClient): void => {
  platformClient = client;
};

export const getStoredPublicClient = (): FederationClient | undefined =>
  publicClient;

export const storePublicClient = (client: FederationClient): void => {
  publicClient = client;
};

/**
 * Test-only: stop in-flight queries and drop the client singletons so each test
 * starts from a fresh client + cache. Without this the module-level singletons
 * leak cache state and late background refetches across tests. Wired into the
 * global `afterEach` in test/vitest.setup.ts.
 */
export const __resetFederationClientsForTests = (): void => {
  authedClient?.stop();
  platformClient?.stop();
  publicClient?.stop();
  authedClient = undefined;
  platformClient = undefined;
  publicClient = undefined;
};
